import { Link } from "react-router-dom";
import { Anchor, ArrowUpRight, Boxes, Clock, Container, Download, FileText, Globe2, HandCoins, Hourglass, Package } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang, usePath } from "@/i18n";

// Export & logistics facts block — used on the combined Export & Quality page.
export const ExportSection = ({ h1 = false, heading = null }) => {
  const { t } = useLang();
  const lp = usePath();
  const E = t.exportPg;
  const H = h1 ? "h1" : "h2";

  const cards = [
    { icon: Package, label: E.packLabel, value: E.packValue, testid: "export-packaging" },
    { icon: Container, label: E.capacityLabel, value: E.capacityValue, testid: "export-capacity" },
    { icon: Hourglass, label: E.shelfLabel, value: E.shelfValue, testid: "export-shelf-life" },
    { icon: Boxes, label: E.moqLabel, value: E.moqValue, testid: "export-moq" },
    { icon: Anchor, label: E.portLabel, value: E.portValue, testid: "export-port" },
    { icon: HandCoins, label: E.incLabel, value: E.incValue, testid: "export-terms" },
    { icon: Clock, label: E.leadLabel, value: E.leadValue, testid: "export-lead-time" },
    { icon: HandCoins, label: E.payLabel, value: E.payValue, testid: "export-payment" },
    { icon: Globe2, label: E.marketsLabel, value: E.marketsValue, testid: "export-markets" },
  ];

  return (
    <section className="bg-clara-espresso text-clara-cream noise-overlay relative py-24 lg:py-36" data-testid="export-section">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="eyebrow text-clara-terracotta mb-6">{E.eyebrow}</p>
          <H className="font-display font-light text-5xl lg:text-7xl leading-[0.95] tracking-tighter max-w-3xl mb-6">{heading ?? E.heading}</H>
          <p className="text-clara-darkmuted leading-relaxed max-w-xl mb-16">{E.intro}</p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-px bg-clara-cream/15 border border-clara-cream/15 mb-px">
          {cards.map((c, i) => (
            <Reveal key={c.testid} delay={i * 0.08} className="bg-clara-espresso">
              <div className="p-8 h-full" data-testid={c.testid}>
                <c.icon className="w-8 h-8 text-clara-terracotta mb-6" strokeWidth={1.25} />
                <p className="font-data text-[10px] tracking-[0.2em] uppercase text-clara-darkmuted mb-2">{c.label}</p>
                <p className="font-display text-2xl leading-snug">{c.value}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="bg-clara-espresso">
          <div className="border border-clara-cream/15 p-8 mb-16" data-testid="export-documents">
            <div className="flex items-start gap-6">
              <FileText className="w-8 h-8 text-clara-terracotta shrink-0 mt-1" strokeWidth={1.25} />
              <div>
                <p className="font-data text-[10px] tracking-[0.2em] uppercase text-clara-darkmuted mb-4">{E.docsLabel}</p>
                <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-2 font-data text-sm text-clara-cream/90">
                  {E.docs.map((d) => (
                    <li key={d} className="border-b border-clara-cream/10 py-2">{d}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="flex flex-col sm:flex-row gap-3">
          <Link to={lp("/contact")} data-testid="export-quote-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-clara-terracotta px-8 py-4 text-sm font-semibold text-clara-cream hover:bg-clara-cream hover:text-clara-espresso transition-colors duration-300">
            {E.cta} <ArrowUpRight className="w-4 h-4" />
          </Link>
          <a href="/clara-catalogue.pdf" download data-testid="export-catalogue-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-clara-cream/30 px-8 py-4 text-sm font-semibold text-clara-cream hover:border-clara-cream transition-colors duration-300">
            <Download className="w-4 h-4" /> {t.catalogueCta}
          </a>
        </div>
      </div>
    </section>
  );
};
